import React from 'react';
import { api, fmt } from '../api';
import { Empty, ErrorBanner, Loading, Pill, statusTone, useAsync } from '../ui';
import { navigate } from '../App';
import { consumeAuthError, PROVIDER_LABELS, ProviderIcon, type SignInProvider } from './SocialSignIn';

type IdentityState = {
  hasPassword: boolean;
  available: SignInProvider[];
  identities: Array<{ provider: SignInProvider; email: string | null; lastUsedAt: string | null }>;
};

function SignInMethods() {
  const identities = useAsync<IdentityState>(() => api('GET', '/api/auth/identities'), []);
  const [message, setMessage] = React.useState<{ tone: 'ok' | 'bad'; text: string } | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const linked = params.get('linked') as SignInProvider | null;
    const authError = consumeAuthError();
    if (authError) return { tone: 'bad', text: authError };
    if (linked && PROVIDER_LABELS[linked]) {
      window.history.replaceState({}, '', '/account');
      return { tone: 'ok', text: `${PROVIDER_LABELS[linked]} is now connected. You can use it to sign in.` };
    }
    return null;
  });
  const [busy, setBusy] = React.useState<SignInProvider | null>(null);

  const disconnect = async (provider: SignInProvider) => {
    setBusy(provider); setMessage(null);
    try {
      await api('DELETE', `/api/auth/identities/${provider}`);
      setMessage({ tone: 'ok', text: `${PROVIDER_LABELS[provider]} was disconnected.` });
      identities.reload();
    } catch (e) {
      setMessage({ tone: 'bad', text: (e as Error).message });
    } finally {
      setBusy(null);
    }
  };

  const state = identities.data;
  const providers = state ? Array.from(new Set<SignInProvider>([
    ...state.available, ...state.identities.map((i) => i.provider),
  ])) : [];
  if (state && providers.length === 0) return null;

  return (
    <section className="card">
      <h2>Sign-in methods</h2>
      <p className="muted">Sign in with Google or Microsoft so you do not need another password. Those accounts can add two-step verification.</p>
      {message && <div className={`banner ${message.tone}`} role={message.tone === 'bad' ? 'alert' : 'status'}>{message.text}</div>}
      {identities.error && <ErrorBanner error={identities.error} />}
      {!state && !identities.error && <p className="muted" role="status">Loading sign-in methods…</p>}
      {state && (
        <ul className="sign-in-methods">
          <li>
            <span className="sign-in-method-name">Email and password</span>
            <span className="muted">{state.hasPassword ? 'Set' : 'Not set. Use “Forgot password?” on the sign-in page to add one.'}</span>
          </li>
          {providers.map((provider) => {
            const linked = state.identities.find((i) => i.provider === provider);
            return (
              <li key={provider}>
                <span className="sign-in-method-name"><ProviderIcon provider={provider} />{PROVIDER_LABELS[provider]}</span>
                <span className="muted">{linked ? `Connected${linked.email ? ` as ${linked.email}` : ''}` : 'Not connected'}</span>
                {linked
                  ? <button onClick={() => disconnect(provider)} disabled={busy !== null}>{busy === provider ? 'Disconnecting…' : 'Disconnect'}</button>
                  : state.available.includes(provider) && (
                    <a className="button-link" href={`/api/auth/oidc/${provider}/start?intent=link`}>Connect</a>
                  )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function AccountView({ onClosed }: { onClosed: () => void }) {
  const [closing, setClosing] = React.useState(false);
  const [confirmation, setConfirmation] = React.useState('');
  const [deleteChild, setDeleteChild] = React.useState<any | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = React.useState('');
  const [success, setSuccess] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const family = useAsync(() => api('GET', '/api/family'), []);

  const exportData = async () => {
    setBusy(true); setError(null);
    try {
      const payload = await api('GET', '/api/family/export');
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = `family-data-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const closeAccount = async () => {
    setBusy(true); setError(null);
    try {
      await api('DELETE', '/api/family', { confirmation });
      onClosed();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const deleteSelectedChild = async () => {
    if (!deleteChild) return;
    setBusy(true); setError(null); setSuccess(null);
    try {
      await api('DELETE', `/api/students/${deleteChild.id}`, { confirmation: deleteConfirmation });
      setSuccess(`${deleteChild.preferred_name}'s profile and assistant were deleted. Other children and your parent account were not changed.`);
      setDeleteChild(null);
      setDeleteConfirmation('');
      family.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SignInMethods />
      <section className="card">
        <h2>Family account</h2>
        <p className="muted">Manage your account data and privacy choices.</p>
        <h3>Download family data</h3>
        <p>Download a copy of student profiles, Student Assistant settings, reminders, and activity history. Authentication credentials and school tokens are excluded.</p>
        <button onClick={exportData} disabled={busy}>Download data</button>
      </section>

      <section className="card">
        <h2>Children and data deletion</h2>
        <p className="muted">
          Delete one child at a time. This removes only that child's profile, Student Assistant, school connections,
          reminders, messages, tasks, activities, and contacts. Other children and the parent account stay active.
        </p>
        {family.loading && !family.data ? <Loading what="children" /> : family.error ? (
          <ErrorBanner error={family.error} onRetry={family.reload} />
        ) : family.data?.students?.length ? (
          <ul className="plain">
            {family.data.students.map((child: any) => (
              <li key={child.id}>
                <div className="spread">
                  <div>
                    <strong>{child.preferred_name}</strong>{' '}
                    <Pill tone={statusTone(child.worker_status)}>{fmt.label(child.worker_status)}</Pill>
                    <div className="muted">
                      Grade {child.grade} · {child.readiness?.assignmentSource?.ready ? 'Canvas ready' : 'Canvas needed'}
                    </div>
                  </div>
                  <div className="row">
                    <button type="button" onClick={() => navigate(`/students/${child.id}`)}>Open setup</button>
                    <button
                      className="danger"
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setDeleteChild(child);
                        setDeleteConfirmation('');
                        setError(null);
                        setSuccess(null);
                      }}
                    >
                      Delete child
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : <Empty>No children are currently added to this account.</Empty>}
        {success && <p className="banner ok" role="status">{success}</p>}
      </section>

      {deleteChild && (
        <section className="card danger-zone">
          <h2>Confirm delete child</h2>
          <p>
            You are deleting <strong>{deleteChild.preferred_name}</strong>. This starts deletion for this child's profile,
            assistant, school tokens, tasks, activities, reminders, messages, and phone/email contacts.
          </p>
          <p className="muted">
            Other children and your parent account will remain. The child data enters the configured retention period
            before scheduled deletion, subject to legal and audit requirements.
          </p>
          <label htmlFor="delete-child-confirmation">
            Type <strong>{deleteChild.preferred_name}</strong> to confirm
          </label>
          <input
            id="delete-child-confirmation"
            value={deleteConfirmation}
            onChange={(event) => setDeleteConfirmation(event.target.value)}
          />
          {error && <div className="banner bad" role="alert">{error}</div>}
          <div className="row">
            <button
              className="danger"
              disabled={busy || deleteConfirmation.toLowerCase() !== deleteChild.preferred_name.toLowerCase()}
              onClick={deleteSelectedChild}
            >
              {busy ? `Deleting ${deleteChild.preferred_name}…` : `Delete ${deleteChild.preferred_name}`}
            </button>
            <button disabled={busy} onClick={() => { setDeleteChild(null); setDeleteConfirmation(''); }}>
              Keep child
            </button>
          </div>
        </section>
      )}

      <section className="card danger-zone">
        <h2>Close family account</h2>
        <p>
          This is the family-level unregister action. It unregisters every child, removes every Student Assistant,
          cancels scheduled notifications and background jobs, revokes connected school access, and signs out all family sessions.
        </p>
        <p className="muted">Account data enters a 30-day retention period before scheduled deletion, subject to legal and audit requirements.</p>
        {!closing ? (
          <button className="danger" onClick={() => setClosing(true)}>Close family account</button>
        ) : (
          <div className="confirmation-panel">
            <label htmlFor="close-confirmation">Type <strong>CLOSE MY FAMILY ACCOUNT</strong> to confirm</label>
            <input id="close-confirmation" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
            {error && <div className="banner bad" role="alert">{error}</div>}
            <div className="row">
              <button className="danger" disabled={busy || confirmation !== 'CLOSE MY FAMILY ACCOUNT'} onClick={closeAccount}>
                {busy ? 'Closing account…' : 'Permanently close account'}
              </button>
              <button disabled={busy} onClick={() => { setClosing(false); setConfirmation(''); }}>Cancel</button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
